import React, { useContext, useState, useEffect, useMemo } from 'react';
import {
	isProductIdWhiteListed,
	JiraProduct,
	JiraProductDiscovery,
	JiraServiceManagementProduct,
	JiraSoftwareProduct,
	JiraWorkManagementProduct,
	makeAri,
} from '../../utils';
import {
	mapProductsToProductSelectOptions,
	mapProductSelectOptionsToProducts,
	mapInviteProductsToProducts,
	getInitialSelectedProducts,
} from './ProductSelectUtils';
import {
	CollectionType,
	type Collections,
	type InviteCapabilitiesObject,
	type InviteProduct,
	type Product,
	type ProductSelectOption,
} from '../../types';
import { defaultInviteApiClient } from '../../clients';
import { useInvitePeopleContext } from '../InvitePeople';
import { ProductSelectAnalytics } from './ProductSelectAnalytics';
import { useAnalytics } from '../../analytics';
import { invitePeopleRendered, invitePeopleRenderedTimings } from '../../analytics/ufoExperiences';
import { useCollections } from '../../experiments/twcForCoreInvites/CollectionsContext';
import { getTwcProducts } from '../../experiments/twcForCoreInvites/getTwcProducts';

type ProductList = Product[];

type ProductsState = {
	availableProductOptions: ProductSelectOption[];
	availableProducts: ProductList;
	hasPermissionToInviteOrRequestAccess: boolean;
	inviteCapabilities: InviteCapabilitiesObject;
	isFetchProductsLoading: boolean;
	selectedProducts: string[];
	hasPreselectedTwc: boolean;
	setAvailableProducts: (products: ProductList) => void;
	setSelectedProducts: (products: string[]) => void;
};

export type ProductsContextProps = {
	resourceAri: string;
	productOptions: ProductSelectOption[] | undefined;
	children: React.ReactNode;
};
export const ProductsStateContext = React.createContext<ProductsState | undefined>(undefined);

export const getAvailableProducts = async (
	cloudId: string,
	resourceAri: string,
	analytics: ProductSelectAnalytics,
): Promise<{
	productOptions: ProductList;
	inviteCapabilities: InviteCapabilitiesObject;
}> => {
	try {
		invitePeopleRendered.mark(invitePeopleRenderedTimings.getInviteCapabilities.startMark);
		const response = await defaultInviteApiClient.inviteCapabilities(cloudId as string);
		invitePeopleRendered.mark(invitePeopleRenderedTimings.getInviteCapabilities.endMark);

		// Get unique capabilities.
		const resources = [
			// Create map (matching keys override) and get just the values
			...new Map(
				[...response.directInvite, ...response.invitePendingApproval].map((item) => [
					item.id,
					item,
				]),
			).values(),
		];
		analytics.getAvailableProductsSucceeded(
			resourceAri,
			resources.map(({ id }) => `ari:cloud:${id}::site/${cloudId}`),
		);
		if (!resources?.length) {
			return {
				productOptions: [],
				inviteCapabilities: { directInvite: [], invitePendingApproval: [] },
			};
		}

		const resourcesAsOptions = mapInviteProductsToProducts(resources).filter((p) =>
			isProductIdWhiteListed(p.id),
		);

		return {
			productOptions: resourcesAsOptions,
			inviteCapabilities: response,
		};
	} catch (err) {
		analytics.getAvailableProductsFailed(resourceAri);
		return {
			productOptions: [],
			inviteCapabilities: { directInvite: [], invitePendingApproval: [] },
		};
	}
};

export const getCollections = async (
	cloudId: string | undefined,
	resourceAri: string,
	analytics: ProductSelectAnalytics,
): Promise<Collections> => {
	if (!cloudId) {
		return {};
	}
	try {
		invitePeopleRendered.mark(invitePeopleRenderedTimings.getCollections.startMark);
		const response = await defaultInviteApiClient.getCollections({ cloudId });
		invitePeopleRendered.mark(invitePeopleRenderedTimings.getCollections.endMark);
		analytics.getCollectionsSucceeded(
			resourceAri,
			!!response?.[CollectionType.TEAMWORK_COLLECTION]?.isEnabled,
		);
		return response;
	} catch (error) {
		analytics.getCollectionsFailed(resourceAri);
		return {};
	}
};

export const ProductContext: React.FC<ProductsContextProps> = ({
	resourceAri,
	children,
	productOptions,
}: ProductsContextProps) => {
	const analytics = useAnalytics(ProductSelectAnalytics);
	const { cloudId, jiraSubProduct, productId } = useInvitePeopleContext();
	const { collections: gqlCollections, shouldFetchCollections } = useCollections();

	const productOptionsFiltered = productOptions
		? mapProductSelectOptionsToProducts(productOptions).filter((p: Product) =>
				isProductIdWhiteListed(p.id),
			)
		: [];

	const [collections, setCollections] = useState<Collections>(gqlCollections);
	const [availableProducts, setAvailableProducts] = useState<ProductList>(productOptionsFiltered);
	const [selectedProducts, setSelectedProducts] = useState<string[]>(
		getInitialSelectedProducts({
			productOptions: availableProducts,
			productId,
			jiraSubProduct,
			collections,
		}),
	);
	const [inviteCapabilities, setInviteCapabilities] = useState<InviteCapabilitiesObject>({
		directInvite: [],
		invitePendingApproval: [],
	});

	const hasPreselectedTwc = useMemo(
		() =>
			getTwcProducts(availableProducts, productId, jiraSubProduct, collections).isTwcPreselected,
		[availableProducts, productId, jiraSubProduct, collections],
	);

	const [isFetchProductsLoading, setIsFetchProductsLoading] = useState(true);
	useEffect(() => {
		const fetchProductData = async () => {
			if (productOptionsFiltered.length === 0 && isFetchProductsLoading) {
				const availableProductsPromise = getAvailableProducts(
					cloudId || '',
					resourceAri,
					analytics,
				);
				const collectionsPromise = shouldFetchCollections
					? getCollections(cloudId, resourceAri, analytics)
					: undefined;
				const [availableProducts, collectionsResponse] = await Promise.all([
					availableProductsPromise,
					collectionsPromise,
				]);
				const { productOptions, inviteCapabilities } = availableProducts;
				setAvailableProducts(productOptions);
				setInviteCapabilities(inviteCapabilities);

				const collections = (shouldFetchCollections ? collectionsResponse : gqlCollections) || {};
				if (shouldFetchCollections) {
					setCollections(collections);
				}
				setSelectedProducts(
					getInitialSelectedProducts({ productOptions, productId, jiraSubProduct, collections }),
				);
			}

			setIsFetchProductsLoading(false);
		};
		fetchProductData();
	}, [
		cloudId,
		productId,
		jiraSubProduct,
		gqlCollections,
		shouldFetchCollections,
		resourceAri,
		isFetchProductsLoading,
		productOptionsFiltered.length,
		analytics,
	]);

	const availableProductOptions = useMemo(
		() =>
			mapProductsToProductSelectOptions(availableProducts, productId, jiraSubProduct, collections),
		[availableProducts, productId, jiraSubProduct, collections],
	);

	const hasPermissionToInviteOrRequestAccess = useMemo((): boolean => {
		if (!productId || !cloudId || isFetchProductsLoading) {
			return false;
		}
		const { directInvite, invitePendingApproval } = inviteCapabilities;
		if (productId === 'platform') {
			return directInvite.length > 0 || invitePendingApproval.length > 0;
		}
		const currentProductId = jiraSubProduct || productId;
		const currentProductAri = makeAri(currentProductId, cloudId);
		const hasRelatedProduct = (config: InviteProduct) => {
			if (productId === JiraProduct.id) {
				if (!jiraSubProduct || jiraSubProduct === 'unknown') {
					return (
						config.ari.indexOf(JiraWorkManagementProduct.id) > 0 ||
						config.ari.indexOf(JiraServiceManagementProduct.id) > 0 ||
						config.ari.indexOf(JiraSoftwareProduct.id) > 0 ||
						config.ari.indexOf(JiraProductDiscovery.id) > 0
					);
				} else if (jiraSubProduct === JiraWorkManagementProduct.id) {
					return (
						config.ari.indexOf(JiraWorkManagementProduct.id) > 0 ||
						config.ari.indexOf(JiraSoftwareProduct.id) > 0
					);
				}
			}
			return config.ari === currentProductAri;
		};
		return directInvite.some(hasRelatedProduct) || invitePendingApproval.some(hasRelatedProduct);
	}, [cloudId, jiraSubProduct, inviteCapabilities, isFetchProductsLoading, productId]);

	return (
		<ProductsStateContext.Provider
			value={{
				selectedProducts,
				availableProducts,
				setAvailableProducts,
				availableProductOptions,
				setSelectedProducts,
				isFetchProductsLoading,
				inviteCapabilities,
				hasPermissionToInviteOrRequestAccess,
				hasPreselectedTwc,
			}}
		>
			{children}
		</ProductsStateContext.Provider>
	);
};

/**
 * Hook that fetches available products and maintains state and logic for which products are currently selected
 */
export const useProducts = () => {
	const state = useContext(ProductsStateContext);

	if (state === undefined) {
		throw new Error('useProducts must be used within a ProductsStateContextProvider');
	}

	return state;
};
